import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import Button from "../ui/button";
import SearchIcon from "../icons/Search";
import useListsObjects from "../../hooks/useListsObjects";
import { useDebounce } from "../../hooks/useDebounce";
import { useBatchAssignSite } from "../../hooks/useEquipment";
import { SiteSummary } from "../../types/user";

type AssignIdsToSiteProps = {
  site: SiteSummary;
};

const PAGE_SIZE = 50;

export default function AssignIdsToSite({ site }: AssignIdsToSiteProps) {
  const intl = useIntl();
  const { isLoading, error: listError, results, data, fetchListObjects } = useListsObjects();
  const {
    error: assignError,
    isLoading: isAssigning,
    response: assignResponse,
    assignSite
  } = useBatchAssignSite();

  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const term = useDebounce(searchInput, 300) ?? "";

  useEffect(() => {
    fetchListObjects({
      assignment: "nonAffected",
      term,
      page,
      limit: PAGE_SIZE
    });
  }, [fetchListObjects, term, page, refreshKey]);

  useEffect(() => {
    if (!assignResponse) return;
    setSelected(new Set());
    setRefreshKey((current) => current + 1);
  }, [assignResponse]);

  const pageUrls = useMemo(
    () => (data ?? []).map((object) => object.url),
    [data]
  );
  const allPageSelected = pageUrls.length > 0 && pageUrls.every((url) => selected.has(url));

  const toggleOne = (url: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allPageSelected) {
        pageUrls.forEach((url) => next.delete(url));
      } else {
        pageUrls.forEach((url) => next.add(url));
      }
      return next;
    });
  };

  const onSubmit = () => {
    if (selected.size === 0 || isAssigning) return;
    assignSite(site.id, [...selected]);
  };

  const totalPages = results?.totalPages ?? 1;

  return (
    <div className="flex flex-col max-h-[80vh]">
      <h3 className="mb-1 text-lg font-semibold text-gray-900">
        {intl.formatMessage({ id: "sites.assignIds.heading" }, { siteName: site.name })}
      </h3>
      <p className="mb-4 text-sm text-gray-600">
        {intl.formatMessage({ id: "sites.assignIds.hint" })}
      </p>

      {assignResponse && (
        <p className={`mb-3 text-sm ${assignResponse.created > 0 ? "text-green-700" : "text-amber-700"}`}>
          {assignResponse.created > 0 && assignResponse.skipped.length === 0
            ? intl.formatMessage(
                { id: "sites.assignIds.success" },
                { count: assignResponse.created, siteName: site.name }
              )
            : assignResponse.created > 0
              ? intl.formatMessage(
                  { id: "sites.assignIds.partial" },
                  { created: assignResponse.created, skipped: assignResponse.skipped.length }
                )
              : intl.formatMessage({ id: "sites.assignIds.noneCreated" })}
        </p>
      )}

      {(listError || assignError) && (
        <p className="mb-3 text-sm text-red-600">
          {(assignError ?? listError)?.message}
        </p>
      )}

      <div className="relative mb-3">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="search"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          placeholder={intl.formatMessage({ id: "sites.assignIds.searchPlaceholder" })}
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="flex items-center justify-between mb-2 text-sm text-gray-600">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={allPageSelected}
            disabled={pageUrls.length === 0}
            onChange={togglePage}
          />
          {intl.formatMessage({ id: "sites.assignIds.selectAll" })}
        </label>
        <span>
          {intl.formatMessage({ id: "sites.assignIds.selectedCount" }, { count: selected.size })}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg min-h-40">
        {isLoading ? (
          <p className="p-4 text-sm text-gray-500">{intl.formatMessage({ id: "common.loading" })}</p>
        ) : data && data.length > 0 ? (
          <ul>
            {data.map((object) => (
              <li key={object.id} className="border-b border-gray-100 last:border-b-0">
                <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selected.has(object.url)}
                    onChange={() => toggleOne(object.url)}
                  />
                  <span className="text-sm text-gray-900 break-all">
                    {object.name ? `${object.name} — ${object.url}` : object.url}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ) : (
          <p className="p-4 text-sm text-gray-500">
            {intl.formatMessage({ id: "sites.assignIds.empty" })}
          </p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <button
            type="button"
            className="text-gray-700 disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            {intl.formatMessage({ id: "sites.assignIds.prev" })}
          </button>
          <span className="text-gray-500">
            {intl.formatMessage({ id: "sites.assignIds.page" }, { page, total: totalPages })}
          </span>
          <button
            type="button"
            className="text-gray-700 disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            {intl.formatMessage({ id: "sites.assignIds.next" })}
          </button>
        </div>
      )}

      <div className="mt-4">
        <Button
          theme="primary"
          size="small"
          disabled={selected.size === 0 || isAssigning}
          loading={isAssigning}
          onClick={onSubmit}
        >
          {intl.formatMessage({ id: "sites.assignIds.submit" })}
        </Button>
      </div>
    </div>
  );
}
